import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  GKButton,
  Grid2,
  Show,
} from "components";
import { useState } from "react";
import { FormProvider, useFormContext, useWatch } from "react-hook-form";
import FormDetails from "./FormDetails";
import FormInputs from "./FormInputs";
import OrderConfirmationModal from "./OrderConfirmationModal";
import OrderSummaryCard from "./OrderSummaryCard";
import RationaleModal from "./RationaleModal";
import useMFOrders, { MFOrderFormValues, useWatchFields } from "./useMFOrders";

interface MutualFundsInnerProps {
  onSuccess?: () => void;
}

const MutualFundsInner = ({ onSuccess }: MutualFundsInnerProps) => {
  const { "extraThings.rightVisibleTable": watchRightVisibleTable } =
    useWatchFields();
  const { control, setValue } = useFormContext<MFOrderFormValues>();
  const items = useWatch({ control, name: "items" }) || [];
  const watchTransactionType = useWatch({ control, name: "transactionType" });
  const [isRationaleOpen, setIsRationaleOpen] = useState(false);
  const [delivery, setDelivery] = useState<{
    rationale: string;
    sendForExecution: boolean;
  } | null>(null);

  const useTopSummaryLayout =
    watchTransactionType === "stp" &&
    (watchRightVisibleTable === "SELECT_PORTFOLIO" ||
      watchRightVisibleTable === "ENTER_AMOUNT_FOLIO" ||
      watchRightVisibleTable === "PLACE_ORDER");

  const handleEditDetails = () => {
    setValue("extraThings.rightVisibleTable", null);
  };

  const isNullState = watchRightVisibleTable === null;
  const isSelectPortfolio = watchRightVisibleTable === "SELECT_PORTFOLIO";
  const isEnterAmount = watchRightVisibleTable === "ENTER_AMOUNT_FOLIO";
  const isPlaceOrder = watchRightVisibleTable === "PLACE_ORDER";
  const isOrderPlaced = watchRightVisibleTable === "ORDER_PLACED";

  const fadeSidebar =
    isSelectPortfolio || isEnterAmount || isPlaceOrder || isOrderPlaced;
  const showBack = isEnterAmount || isPlaceOrder;
  const showFooterJustifyBetween = showBack;

  const nextDisabled = (() => {
    if (isSelectPortfolio) return items.length === 0;
    if (isEnterAmount)
      return !items.some(
        (item) =>
          (item.amount != null && item.amount !== 0) ||
          (item.units != null && item.units !== 0),
      );
    return false;
  })();

  const handleNext = () => {
    if (isSelectPortfolio) {
      setValue("extraThings.rightVisibleTable", "ENTER_AMOUNT_FOLIO");
    } else if (isEnterAmount) {
      setValue("extraThings.rightVisibleTable", "PLACE_ORDER");
    }
  };

  const handleOpenRationale = () => {
    setIsRationaleOpen(true);
  };

  const handleSelectDelivery = (
    rationale: string,
    sendForExecution: boolean,
  ) => {
    setValue("rationale", rationale);
    // Keep the rationale modal mounted; the confirmation modal opens on top.
    setDelivery({ rationale, sendForExecution });
  };

  const handleOrderSuccess = () => {
    setDelivery(null);
    setIsRationaleOpen(false);
    onSuccess?.();
  };

  const handleBack = () => {
    if (isEnterAmount) {
      setValue("extraThings.rightVisibleTable", "SELECT_PORTFOLIO");
    } else if (isPlaceOrder) {
      setValue("extraThings.rightVisibleTable", "ENTER_AMOUNT_FOLIO");
    }
  };

  const footer = (
    <Grid2
      size={12}
      p={"12px 24px"}
      display={"flex"}
      alignItems={"center"}
      justifyContent={showFooterJustifyBetween ? "space-between" : "flex-end"}
      borderTop={"1px solid var(--neutral-100)"}
      bgcolor={"var(--white)"}
    >
      <Show.When isTrue={showBack}>
        <GKButton
          variant="outlined"
          color="dark"
          startIcon={<ArrowLeftIcon />}
          onClick={handleBack}
        >
          Back
        </GKButton>
      </Show.When>
      {isPlaceOrder ? (
        <GKButton
          variant="contained"
          startIcon={<CheckIcon height={16} width={16} />}
          onClick={handleOpenRationale}
        >
          Place Order
        </GKButton>
      ) : (
        <GKButton
          variant="contained"
          endIcon={<ArrowRightIcon />}
          disabled={nextDisabled}
          onClick={handleNext}
        >
          Next
        </GKButton>
      )}
    </Grid2>
  );

  return (
    <form style={{ height: "100%", width: "100%" }}>
      {useTopSummaryLayout ? (
        <Grid2
          container
          direction="column"
          sx={{ height: "100%", width: "100%" }}
        >
          <Grid2
            size={12}
            sx={{
              flex: 1,
              overflow: "auto",
              p: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <OrderSummaryCard onEdit={handleEditDetails} />
            <FormDetails />
          </Grid2>
          <Show.When isTrue={!isNullState}>{footer}</Show.When>
        </Grid2>
      ) : (
        <Grid2 container sx={{ height: "100%", width: "100%" }}>
          <Grid2
            size={3.5}
            p={3}
            borderRight={"1px solid var(--neutral-100)"}
            sx={{
              height: "100%",
              overflow: "auto",
              opacity: fadeSidebar ? 0.33 : 1,
              transition: "all 0.3s",
              "&:hover": { opacity: 1 },
            }}
          >
            <FormInputs />
          </Grid2>
          <Grid2 size={8.5} sx={{ height: "100%", overflow: "auto" }}>
            <Grid2 size={12} p={3} minHeight={"calc(100vh - 126px)"}>
              <FormDetails />
            </Grid2>
            <Show.When isTrue={!isNullState}>{footer}</Show.When>
          </Grid2>
        </Grid2>
      )}

      <Show.When isTrue={isRationaleOpen}>
        <RationaleModal
          open={isRationaleOpen}
          onClose={() => setIsRationaleOpen(false)}
          onSelect={handleSelectDelivery}
        />
      </Show.When>

      <Show.When isTrue={!!delivery}>
        <OrderConfirmationModal
          open={!!delivery}
          setOpen={(value) => !value && setDelivery(null)}
          rationale={delivery?.rationale ?? ""}
          sendForExecution={delivery?.sendForExecution ?? true}
          onSuccess={handleOrderSuccess}
        />
      </Show.When>
    </form>
  );
};

interface MutualFundsProps {
  onSuccess?: () => void;
}

const MutualFunds = ({ onSuccess }: MutualFundsProps) => {
  const { form } = useMFOrders();

  return (
    <FormProvider {...form}>
      <MutualFundsInner onSuccess={onSuccess} />
    </FormProvider>
  );
};

export default MutualFunds;
